import { useState, useRef, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { FileText, ChevronDown, Check } from 'lucide-react'
import { useDocumentTypes } from '../../hooks/useOrganization'

interface DocumentTypePickerProps {
  orgKoblinkId: string
  selectedId?: number | null
  onSelect: (id: number | null) => void
}

export function DocumentTypePicker({ orgKoblinkId, selectedId, onSelect }: DocumentTypePickerProps) {
  const { t } = useTranslation()
  const { data: types, isLoading } = useDocumentTypes(orgKoblinkId)

  const [isOpen, setIsOpen] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!isOpen) return
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [isOpen])

  const selectedType = types?.find((dt) => dt.id === selectedId)
  const displayName = selectedType?.name ?? t('metadata.selectType')

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        disabled={isLoading}
        className="w-full flex items-center gap-1.5 px-2 py-1.5 text-xs rounded-md border border-neutral-200 dark:border-neutral-700 bg-neutral-50 dark:bg-neutral-800 text-neutral-900 dark:text-neutral-100 hover:border-neutral-300 dark:hover:border-neutral-600 transition-colors"
      >
        {selectedType ? (
          <span
            className="shrink-0 w-2.5 h-2.5 rounded"
            style={{ backgroundColor: selectedType.color || '#6B7280' }}
          />
        ) : (
          <FileText size={12} className="shrink-0 text-neutral-400" />
        )}
        <span className={`flex-1 text-left truncate ${!selectedType ? 'text-neutral-400' : ''}`}>
          {displayName}
        </span>
        <ChevronDown size={10} className="shrink-0 text-neutral-400" />
      </button>

      {isOpen && (
        <div className="absolute left-0 top-full mt-1 w-full bg-white dark:bg-neutral-800 border border-neutral-200 dark:border-neutral-700 rounded-lg shadow-lg z-50 py-1 max-h-[180px] overflow-y-auto">
          {/* No type option */}
          <button
            onClick={() => {
              onSelect(null)
              setIsOpen(false)
            }}
            className="w-full flex items-center gap-1.5 px-2 py-1.5 text-xs text-left hover:bg-neutral-50 dark:hover:bg-neutral-700/50 transition-colors text-neutral-500 dark:text-neutral-400"
          >
            <span className="flex-1">{t('metadata.noneSelected')}</span>
            {selectedId == null && <Check size={10} className="text-primary-500" />}
          </button>

          {types?.map((dt) => (
            <button
              key={dt.id}
              onClick={() => {
                onSelect(dt.id)
                setIsOpen(false)
              }}
              className="w-full flex items-center gap-1.5 px-2 py-1.5 text-xs text-left hover:bg-neutral-50 dark:hover:bg-neutral-700/50 transition-colors"
            >
              <span
                className="shrink-0 w-2.5 h-2.5 rounded"
                style={{ backgroundColor: dt.color || '#6B7280' }}
              />
              <span className="flex-1 truncate text-neutral-900 dark:text-neutral-100">
                {dt.name}
              </span>
              {dt.id === selectedId && <Check size={10} className="text-primary-500" />}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}
